import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Users, Target, Award, Globe, Zap, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';

const AboutPage: React.FC = () => {
  const teamMembers = [
    {
      name: 'Alhassan Aly',
      role: 'CEO & Founder',
      bio: 'Visionary leader with a passion for building AI-powered solutions that empower businesses.',
      avatar: 'AA'
    },
    {
      name: 'Pritomrit Bora',
      role: 'CTO & Founder',
      bio: 'The mastermind behind our cutting-edge AI content generation and social media analytics.',
      avatar: 'PB'
    }
  ];

  const values = [
    {
      icon: Users,
      title: 'User-Centric',
      description: 'Every feature is designed with our users\' success in mind.'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'We leverage cutting-edge AI to solve real marketing challenges.'
    },
    {
      icon: Shield,
      title: 'Trust & Security',
      description: 'Your data and privacy are our top priorities.'
    },
    {
      icon: Globe,
      title: 'Global Impact',
      description: 'Empowering businesses worldwide to connect with their audiences.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50/50 via-background to-purple-50/50 dark:from-slate-900/50 dark:via-background dark:to-slate-800/50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            About Quixocial
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            We're on a mission to revolutionize social media management by combining powerful AI technology
            with intuitive design, helping businesses create engaging content and build meaningful connections.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="text-white" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)' }}>
                Start Your Journey
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">Our Mission</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Social media marketing shouldn't be overwhelming. We believe every business deserves access to
                professional-grade tools that make content creation effortless and effective.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                By harnessing the power of artificial intelligence and maintaining a focus on user experience,
                we're democratizing social media marketing for businesses of all sizes.
              </p>

            </div>
            <div className="bg-gradient-to-br from-blue-100/50 to-purple-100/50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-8 border border-border">
              <Target className="w-16 h-16 mb-6" style={{ color: '#30b3dd' }} />
              <h3 className="text-2xl font-bold text-foreground mb-4">Our Vision</h3>
              <p className="text-muted-foreground">
                To become the world's most trusted AI-powered social media management platform,
                enabling businesses to create authentic connections and drive meaningful engagement
                across all digital channels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4">Our Values</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              These core principles guide everything we do and shape how we build products for our community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center border-border shadow-lg hover:shadow-xl transition-shadow bg-card">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)' }}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-card-foreground mb-3">{value.title}</h3>
                    <p className="text-muted-foreground">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4">Meet Our Team</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We're a passionate team of marketers, engineers, and designers united by our mission to
              transform social media management.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {teamMembers.map((member, index) => (
              <Card key={index} className="text-center border-border shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-card">
                <CardContent className="p-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)' }}>
                    {member.avatar}
                  </div>
                  <h3 className="text-xl font-semibold text-card-foreground mb-1">{member.name}</h3>
                  <p className="font-medium mb-3" style={{ color: '#30b3dd' }}>{member.role}</p>
                  <p className="text-muted-foreground text-sm">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <div className="space-y-4 text-slate-300">
              <p>
                Our journey began with a simple realization: content creation and social media management had become too fragmented, time-consuming, and complicated for small businesses and creators.
              </p>
              <p>
                We believe it's time to revolutionize how brands and creators build their digital presence. Today, creating content, and managing social media often means switching between multiple tools, making it hard to stay consistent and on-brand across every channel. We envision a smarter, unified approach that empowers businesses and creators to manage everything seamlessly in one place.
              </p>
              <p>
                That's why we're building an all-in-one platform that brings together content creation, post design, scheduling, analytics, and insights — powered by automation and intelligent customization. Our technology automatically adapts content to each brand's unique style and identity, ensuring a consistent, recognizable presence across all platforms.
              </p>
              <p>
                Our mission is to simplify the way creators and businesses manage their social media while helping them grow with confidence and clarity. By combining automation, analytics, and brand intelligence, we enable them to focus on what truly matters — creating exceptional products, telling authentic stories, and building lasting connections with their audiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Mode */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="hero-title text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            <span style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Ready to Join Our Mission?
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed">
            Be among the early adopters shaping the future of social with Quixocial.
          </p>

          <div className="flex justify-center px-4 sm:px-0">
            <Link to="/register" className="w-full sm:w-auto max-w-sm sm:max-w-none">
              <motion.div
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
                className="group relative"
              >
                <div className="absolute -inset-1 rounded-2xl blur opacity-30 group-hover:opacity-70 transition duration-500" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a, #4a298a)' }}></div>
                <Button
                  size="lg"
                  className="relative w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-semibold shadow-lg hover:shadow-2xl rounded-2xl btn-interactive ripple overflow-hidden text-white"
                  style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
                >
                  <span className="relative z-10">Start Free</span>
                  <ArrowRight className="ml-3 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;